import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useRef, type ReactNode, type ComponentProps } from "react";
import { Button } from "ui";

export type AlertModalProps = {
  isOpen: boolean;
  onClose(): void;
  title: string;
  description: ReactNode;
  Icon: (props: ComponentProps<"svg">) => JSX.Element;
  variant: "danger" | "warning" | "success";
  confirmLabel: string;
  onCancel?: () => void;
  children?: ReactNode;
  disableButtonVariant?: boolean;
} & (
  | {
      onConfirm: () => void;
      customConfirmFn?: never;
    }
  | {
      onConfirm?: never;
      customConfirmFn: () => void;
    }
);

export default function AlertModal({
  isOpen,
  onClose,
  title,
  description,
  Icon,
  variant,
  confirmLabel,
  onConfirm,
  onCancel,
  children,
  disableButtonVariant,
  customConfirmFn,
}: AlertModalProps) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-20 overflow-y-auto"
          initialFocus={confirmBtnRef}
          onClose={onClose}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-foundations-subtler/50 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:h-screen sm:align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative inline-block overflow-hidden rounded-lg bg-foundations-default text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
                <div className="bg-foundations-default px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div
                      className={`mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full  sm:mx-0 sm:h-10 sm:w-10 ${iconVariantClassName[variant]}`}
                    >
                      <Icon
                        className="h-6 w-6 text-current"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-foundations-boldest"
                      >
                        {title}
                      </Dialog.Title>
                      <div className="mt-2">
                        <div className="text-sm font-normal text-foundations-subtle">
                          {description}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {children && (
                  <div className="mb-4 mt-2 px-4 sm:px-6">{children}</div>
                )}

                <div className="flex flex-col-reverse gap-3 px-4 pb-6 pt-2 sm:flex-row sm:px-6">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                      onCancel?.();
                    }}
                    className="flex-1"
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      customConfirmFn
                        ? customConfirmFn
                        : (e) => {
                            e.stopPropagation();
                            onConfirm();
                            onClose();
                          }
                    }
                    ref={confirmBtnRef}
                    className="flex-1"
                    variant="primary"
                    colorScheme={
                      disableButtonVariant || variant !== "danger"
                        ? undefined
                        : "danger"
                    }
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

const iconVariantClassName: Record<AlertModalProps["variant"], string> = {
  danger: "bg-danger-subtler text-danger",
  warning: "bg-warning-subtler text-warning",
  success: "bg-success-subtler text-success",
};
